import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/api";
import { BrandLogo } from "@/components/Brand";
import Report from "@/components/steps/Report";
import logo from "@/assets/farma-logo.png";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import {
  LayoutDashboard, CheckCircle2, SlidersHorizontal, BarChart3, Settings as SettingsIcon,
  Shield, LogOut, Lock, RefreshCw, Save, QrCode, Printer, Download as DownloadIcon, Eye,
} from "lucide-react";

const GRADES = [
  { v: "CONCORDE", l: "Concorde" },
  { v: "PARZIALMENTE_CONCORDE", l: "Parzialmente concorde" },
  { v: "NON_CONCORDE", l: "Non concorde" },
  { v: "NON_VALUTABILE", l: "Non valutabile" },
];
const SEV_IT = { none: "Assente", mild: "Lieve", moderate: "Moderato", evident: "Evidente", not_evaluable: "Non valutabile" };
const CONF_IT = { good: "buona", limited: "limitata", insufficient: "insufficiente" };

const TABS = [
  { id: "overview", label: "Panoramica", icon: LayoutDashboard },
  { id: "validation", label: "Validazione", icon: CheckCircle2 },
  { id: "parameters", label: "Parametri", icon: SlidersHorizontal },
  { id: "stats", label: "Statistiche", icon: BarChart3 },
  { id: "settings", label: "Impostazioni", icon: SettingsIcon },
  { id: "poster", label: "Poster QR", icon: QrCode },
  { id: "preview", label: "Anteprima report", icon: Eye },
  { id: "privacy", label: "Privacy tecnica", icon: Shield },
];

const SAMPLE_REPORT = {
  date: new Date().toISOString(),
  age: "35–44 anni",
  observations: [
    { parameter: "redness", label: "Rossore apparente", severity_it: "Moderato", detail: "Si osservano un rossore diffuso, più evidenti nell'area: guance.", evaluable: true },
    { parameter: "pores", label: "Pori visibili", severity_it: "Lieve", detail: "Sembrano osservabili pori più visibili, più evidenti nell'area: naso e zona centrale del viso.", evaluable: true },
    { parameter: "fine_lines", label: "Linee e rughe visibili", severity_it: "Lieve", detail: "Sembrano osservabili alcune linee sottili, più evidenti nell'area: zona perioculare.", evaluable: true },
    { parameter: "brightness", label: "Luminosità apparente", severity_it: "Non valutabile", detail: "Le condizioni di illuminazione non permettono una valutazione sufficientemente attendibile.", evaluable: false },
  ],
  questionnaire_summary: [
    "La pelle tende a tirare leggermente dopo la detersione",
    "Noti lucidità prevalentemente nella zona T",
    "Ti consideri anche di pelle sensibile",
    "Il tuo obiettivo principale è un incarnato più uniforme",
    "Utilizzi già: vitamina C",
  ],
  priorities: [
    { title: "PROTEZIONE QUOTIDIANA", text: "Una protezione solare regolare aiuta a preservare l'uniformità e l'aspetto della pelle." },
    { title: "UNIFORMITÀ E LUMINOSITÀ", text: "Il tuo obiettivo dichiarato e alcune caratteristiche visibili suggeriscono di concentrare la routine su uniformità e luminosità." },
    { title: "COMFORT CUTANEO", text: "Le sensazioni che riferisci dopo la detersione suggeriscono di privilegiare una routine delicata." },
  ],
  limitations: ["Alcune aree presentano ombre che possono influenzare la valutazione."],
};

function AnteprimaReport() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { adminApi.get("/admin/settings").then((r) => setSettings(r.data)); }, []);
  if (!settings) return null;
  return (
    <div data-testid="report-preview">
      <div className="mb-4 p-3 rounded-xl bg-[var(--fs-tint)] border border-[#BFDBFE] text-sm text-[var(--fs-deep)]">
        Anteprima con dati di esempio: è esattamente ciò che vede il cliente al termine dello Skin Check.
      </div>
      <div className="rounded-3xl overflow-hidden border border-[var(--fs-border)]">
        <Report report={SAMPLE_REPORT} settings={settings} sessionId={null} preview onRestart={() => {}} />
      </div>
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await adminApi.post("/admin/login", { email, password });
      localStorage.setItem("fs_admin_token", data.token);
      onLogin();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Accesso non riuscito");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-[100dvh] grid place-items-center bg-[var(--fs-page)] px-5">
      <form onSubmit={submit} className="w-full max-w-sm fs-card p-7" data-testid="admin-login-form">
        <BrandLogo className="h-8" />
        <div className="flex items-center gap-2 mt-6 text-[var(--fs-deep)]">
          <Lock size={18} /> <h1 className="text-lg font-bold">Area riservata</h1>
        </div>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required
          className="w-full mt-5 p-3.5 rounded-xl border border-[var(--fs-border)] bg-white fs-focus" data-testid="admin-email-input" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required
          className="w-full mt-3 p-3.5 rounded-xl border border-[var(--fs-border)] bg-white fs-focus" data-testid="admin-password-input" />
        {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
        <button disabled={busy} className="fs-btn-primary w-full py-3.5 mt-5 fs-focus" data-testid="admin-login-button">
          {busy ? "Accesso…" : "Accedi"}
        </button>
      </form>
    </div>
  );
}

const StatCard = ({ label, value, sub }) => (
  <div className="fs-card p-4">
    <p className="text-xs text-[var(--fs-muted)] uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold mt-1 text-[var(--fs-ink)]">{value}</p>
    {sub && <p className="text-xs text-[var(--fs-muted)] mt-0.5">{sub}</p>}
  </div>
);

function Overview() {
  const [period, setPeriod] = useState("total");
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.get(`/admin/analytics?period=${period}`).then((r) => setData(r.data)); }, [period]);
  const periods = [["today", "Oggi"], ["7d", "7 giorni"], ["30d", "30 giorni"], ["total", "Totale"]];
  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-5">
        {periods.map(([v, l]) => (
          <button key={v} onClick={() => setPeriod(v)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border ${period === v ? "bg-[var(--fs-blue)] text-white border-[var(--fs-blue)]" : "bg-white border-[var(--fs-border)] text-[var(--fs-ink-2)]"}`}
            data-testid={`period-${v}`}>{l}</button>
        ))}
      </div>
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="overview-stats">
          <StatCard label="Skin Check iniziati" value={data.started} />
          <StatCard label="Skin Check completati" value={data.completed} />
          <StatCard label="Completamento" value={`${data.completion_rate}%`} />
          <StatCard label="Foto rifiutate" value={data.quality_rejected} />
          <StatCard label="Report salvati" value={data.reports_saved} />
          <StatCard label="Invii WhatsApp" value={data.whatsapp_sent} />
          <StatCard label="Click indicazioni" value={data.directions_clicked} />
          <StatCard label="Più spesso non valutabile" value={data.most_not_evaluable?.label || "—"} sub={data.most_not_evaluable?.count ? `${data.most_not_evaluable.count} volte` : ""} />
        </div>
      )}
    </div>
  );
}

function Validation() {
  const [sessions, setSessions] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [grades, setGrades] = useState({});
  const load = useCallback(() => adminApi.get("/admin/sessions").then((r) => setSessions(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const open = async (sid) => {
    setSel(sid);
    const { data } = await adminApi.get(`/admin/sessions/${sid}`);
    setDetail(data);
    setGrades(data.validation_grades || {});
  };
  const save = async () => {
    await adminApi.post("/admin/validate", { session_id: sel, grades });
    load();
    alert("Valutazione salvata");
  };
  return (
    <div className="grid lg:grid-cols-[300px_1fr] gap-5">
      <div className="fs-card p-3 max-h-[70vh] overflow-auto no-scrollbar">
        <div className="flex items-center justify-between px-1 mb-2">
          <span className="text-sm font-semibold">Sessioni</span>
          <button onClick={load} className="text-[var(--fs-muted)]" data-testid="reload-sessions"><RefreshCw size={14} /></button>
        </div>
        {sessions.length === 0 && <p className="text-xs text-[var(--fs-muted)] p-2">Nessuna sessione ancora.</p>}
        {sessions.map((s) => (
          <button key={s.session_id} onClick={() => open(s.session_id)}
            className={`w-full text-left p-3 rounded-xl mb-1.5 border text-sm ${sel === s.session_id ? "border-[var(--fs-blue)] bg-[var(--fs-tint)]" : "border-transparent hover:bg-slate-50"}`}
            data-testid={`session-item-${s.session_id}`}>
            <div className="font-mono text-xs truncate">{s.session_id.slice(0, 12)}…</div>
            <div className="text-xs text-[var(--fs-muted)] mt-0.5">{s.n_observations} oss. · {s.status}{s.validated ? " · ✓" : ""}</div>
          </button>
        ))}
      </div>

      <div>
        {!detail ? (
          <div className="fs-card p-8 text-center text-sm text-[var(--fs-muted)]">Seleziona una sessione per ispezionare l'output strutturato.</div>
        ) : (
          <div data-testid="session-detail">
            <div className="fs-card p-4 mb-4">
              <div className="text-xs text-[var(--fs-muted)]">SESSION_ID</div>
              <div className="font-mono text-sm">{detail.session_id}</div>
              <div className="mt-2 text-xs text-[var(--fs-ink-2)]">
                Qualità: {Object.entries(detail.quality || {}).map(([v, q]) => `${v}:${q.status}`).join("  ") || "—"}
              </div>
            </div>

            <div className="space-y-3">
              {detail.consensus.map((c) => (
                <div key={c.parameter} className="fs-card p-4" data-testid={`consensus-${c.parameter}`}>
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">{c.label}</h4>
                    <span className="text-xs font-bold text-[var(--fs-deep)]">{SEV_IT[c.final_severity]} · conf. {CONF_IT[c.final_confidence]}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div className="rounded-lg bg-slate-50 p-2">
                      <div className="text-[var(--fs-muted)] mb-0.5">Gemini 3.1 Pro</div>
                      <div>{SEV_IT[c.gemini.severity]} · {CONF_IT[c.gemini.confidence]}</div>
                      <div className="text-[var(--fs-muted)]">viste: {c.gemini.views.join(",") || "—"}</div>
                    </div>
                    <div className="rounded-lg bg-slate-50 p-2">
                      <div className="text-[var(--fs-muted)] mb-0.5">GPT-5.4</div>
                      <div>{SEV_IT[c.openai.severity]} · {CONF_IT[c.openai.confidence]}</div>
                      <div className="text-[var(--fs-muted)]">viste: {c.openai.views.join(",") || "—"}</div>
                    </div>
                  </div>
                  {c.reason && <p className="text-xs text-[var(--fs-muted)] mt-2">Consenso: {c.reason}</p>}
                  <select value={grades[c.parameter] || ""} onChange={(e) => setGrades((g) => ({ ...g, [c.parameter]: e.target.value }))}
                    className="w-full mt-3 p-2 rounded-lg border border-[var(--fs-border)] text-sm bg-white" data-testid={`grade-${c.parameter}`}>
                    <option value="">— Valutazione staff —</option>
                    {GRADES.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="fs-card p-4 mt-4">
              <div className="text-xs font-semibold mb-2">Rendering pubblico</div>
              {detail.public_observations.map((o, i) => (
                <div key={i} className="text-xs mb-1"><b>{o.label}</b> — {o.severity_it}: {o.detail}</div>
              ))}
              {detail.limitations?.length > 0 && <div className="text-xs text-[var(--fs-muted)] mt-2">Limiti: {detail.limitations.join(" · ")}</div>}
            </div>

            <button onClick={save} className="fs-btn-primary w-full py-3 mt-4 flex items-center justify-center gap-2 fs-focus" data-testid="save-validation-button">
              <Save size={16} /> Salva valutazione
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const PARAM_LABELS = {
  redness: "Rossore apparente", pigmentation: "Discromie visibili", pores: "Pori visibili",
  blemishes: "Imperfezioni visibili", fine_lines: "Linee e rughe visibili", texture: "Texture superficiale",
  evenness: "Uniformità dell'incarnato", brightness: "Luminosità apparente", dark_circles: "Occhiaie visibili",
};

function Parameters() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { adminApi.get("/admin/settings").then((r) => setSettings(r.data)); }, []);
  const toggle = async (k) => {
    const ep = { ...settings.enabled_visual_parameters, [k]: !settings.enabled_visual_parameters[k] };
    const { data } = await adminApi.put("/admin/settings", { data: { enabled_visual_parameters: ep } });
    setSettings(data);
  };
  if (!settings) return null;
  return (
    <div>
      <p className="text-sm text-[var(--fs-ink-2)] mb-4">Attiva o disattiva ogni parametro visivo senza modificare il codice. Un parametro disattivato non viene analizzato né mostrato.</p>
      <div className="space-y-2.5">
        {Object.keys(PARAM_LABELS).map((k) => (
          <div key={k} className="fs-card p-4 flex items-center justify-between" data-testid={`param-row-${k}`}>
            <span className="text-sm font-medium">{PARAM_LABELS[k]}</span>
            <button onClick={() => toggle(k)}
              className={`w-12 h-7 rounded-full transition-colors relative ${settings.enabled_visual_parameters[k] ? "bg-[var(--fs-blue)]" : "bg-slate-300"}`}
              data-testid={`param-toggle-${k}`}>
              <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${settings.enabled_visual_parameters[k] ? "left-6" : "left-1"}`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stats() {
  const [dash, setDash] = useState(null);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [rep, setRep] = useState(null);
  useEffect(() => { adminApi.get("/admin/validation-dashboard").then((r) => setDash(r.data)); }, []);
  const compare = async () => {
    const { data } = await adminApi.post("/admin/repeatability", { scan_a: a.trim(), scan_b: b.trim() });
    setRep(data.rows);
  };
  return (
    <div className="space-y-6">
      {dash && (
        <div>
          <h3 className="font-semibold mb-3">Concordanza interna nei test Farma Store</h3>
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <StatCard label="Test valutati" value={dash.tests} />
            <StatCard label="Output non valutabili" value={dash.not_evaluable_total} />
            <StatCard label="Rifiuti quality gate" value={`${dash.quality_rejection_rate}%`} />
          </div>
          <div className="fs-card p-4">
            {dash.per_parameter.length === 0 && <p className="text-sm text-[var(--fs-muted)]">Nessuna valutazione staff registrata.</p>}
            {dash.per_parameter.map((p) => (
              <div key={p.parameter} className="flex items-center justify-between py-2 border-b border-[var(--fs-border)] last:border-0 text-sm">
                <span>{p.label}</span>
                <span className="font-semibold tabular-nums">{p.concordi} / {p.totali} concordi</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[var(--fs-muted)] mt-2">I campioni ridotti non costituiscono validazione statistica.</p>
        </div>
      )}

      <div>
        <h3 className="font-semibold mb-3">Test di ripetibilità (Scan A / Scan B)</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={a} onChange={(e) => setA(e.target.value)} placeholder="SESSION_ID Scan A" className="flex-1 p-3 rounded-xl border border-[var(--fs-border)] text-sm font-mono" data-testid="repeat-a" />
          <input value={b} onChange={(e) => setB(e.target.value)} placeholder="SESSION_ID Scan B" className="flex-1 p-3 rounded-xl border border-[var(--fs-border)] text-sm font-mono" data-testid="repeat-b" />
          <button onClick={compare} className="fs-btn-primary px-5 py-3 fs-focus" data-testid="repeat-compare">Confronta</button>
        </div>
        {rep && (
          <div className="fs-card p-4 mt-4" data-testid="repeat-results">
            {rep.map((r) => (
              <div key={r.parameter} className="flex items-center justify-between py-2 border-b border-[var(--fs-border)] last:border-0 text-sm">
                <span>{r.label}</span>
                <span className="text-[var(--fs-muted)]">{r.scan_a} / {r.scan_b}</span>
                <span className="font-semibold">{r.result.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, textarea }) => (
  <div>
    <label className="text-xs font-medium text-[var(--fs-muted)] uppercase tracking-wide">{label}</label>
    {textarea ? (
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full mt-1 p-3 rounded-xl border border-[var(--fs-border)] text-sm bg-white resize-none" />
    ) : (
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 p-3 rounded-xl border border-[var(--fs-border)] text-sm bg-white" />
    )}
  </div>
);

function SettingsTab() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => { adminApi.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const { data } = await adminApi.put("/admin/settings", { data: s });
    setS(data);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  if (!s) return null;
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="fs-card p-4 flex items-center justify-between" data-testid="app-mode-row">
        <div>
          <p className="text-sm font-semibold">Modalità applicazione</p>
          <p className="text-xs text-[var(--fs-muted)]">test: strumenti di validazione attivi · public: esperienza pubblica</p>
        </div>
        <div className="flex gap-2">
          {["test", "public"].map((m) => (
            <button key={m} onClick={() => set("app_mode", m)}
              className={`px-4 py-2 rounded-full text-sm font-medium border ${s.app_mode === m ? "bg-[var(--fs-blue)] text-white border-[var(--fs-blue)]" : "bg-white border-[var(--fs-border)]"}`}
              data-testid={`mode-${m}`}>{m}</button>
          ))}
        </div>
      </div>

      <div className="fs-card p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold">Soglia minima di confidenza</p>
          <p className="text-xs text-[var(--fs-muted)]">confidenza minima mostrata pubblicamente</p>
        </div>
        <div className="flex gap-2">
          {[["limited", "limitata"], ["good", "buona"]].map(([m, l]) => (
            <button key={m} onClick={() => set("min_confidence", m)}
              className={`px-4 py-2 rounded-full text-sm font-medium border ${s.min_confidence === m ? "bg-[var(--fs-blue)] text-white border-[var(--fs-blue)]" : "bg-white border-[var(--fs-border)]"}`}
              data-testid={`conf-${m}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="fs-card p-5 space-y-4">
        <Field label="Numero WhatsApp (senza +)" value={s.whatsapp_number} onChange={(v) => set("whatsapp_number", v)} />
        <Field label="Intro WhatsApp (prima riga del profilo)" value={s.whatsapp_intro} onChange={(v) => set("whatsapp_intro", v)} textarea />
        <Field label="Messaggio WhatsApp generico" value={s.whatsapp_message} onChange={(v) => set("whatsapp_message", v)} textarea />
        <Field label="Nome negozio" value={s.store_name} onChange={(v) => set("store_name", v)} />
        <Field label="Indirizzo" value={s.store_address} onChange={(v) => set("store_address", v)} />
        <Field label="URL mappa" value={s.maps_url} onChange={(v) => set("maps_url", v)} />
        <Field label="Dominio pubblico" value={s.public_domain} onChange={(v) => set("public_domain", v)} />
      </div>

      <div className="fs-card p-5 space-y-4">
        <Field label="Titolo CTA" value={s.cta_headline} onChange={(v) => set("cta_headline", v)} />
        <Field label="Testo CTA" value={s.cta_copy} onChange={(v) => set("cta_copy", v)} textarea />
        <Field label="Footer report" value={s.report_footer} onChange={(v) => set("report_footer", v)} />
      </div>

      <div className="fs-card p-5 space-y-4">
        <Field label="Disclaimer principale" value={s.disclaimer_main} onChange={(v) => set("disclaimer_main", v)} textarea />
        <Field label="Disclaimer influenze" value={s.disclaimer_influence} onChange={(v) => set("disclaimer_influence", v)} textarea />
        <Field label="Disclaimer no-fisiologia" value={s.disclaimer_no_physio} onChange={(v) => set("disclaimer_no_physio", v)} textarea />
        <Field label="Testo privacy" value={s.privacy_text} onChange={(v) => set("privacy_text", v)} textarea />
      </div>

      <button onClick={save} className="fs-btn-primary w-full py-3.5 flex items-center justify-center gap-2 fs-focus" data-testid="save-settings-button">
        <Save size={16} /> {saved ? "Salvato ✓" : "Salva impostazioni"}
      </button>
    </div>
  );
}

function QRPoster() {
  const [settings, setSettings] = useState(null);
  const [url, setUrl] = useState("");
  const [qr, setQr] = useState("");
  const [theme, setTheme] = useState("chiaro");
  const T = theme === "scuro"
    ? { bg: "#1E3A8A", ink: "#FFFFFF", ink2: "#DBEAFE", eyebrow: "#93C5FD", cta: "#FFFFFF", meta: "#BFDBFE", border: "rgba(255,255,255,0.25)", qrBorder: "rgba(255,255,255,0.4)" }
    : { bg: "#FFFFFF", ink: "#0F172A", ink2: "#475569", eyebrow: "#2563EB", cta: "#1E3A8A", meta: "#94A3B8", border: "#E2E8F0", qrBorder: "#E2E8F0" };
  useEffect(() => {
    adminApi.get("/admin/settings").then((r) => {
      setSettings(r.data);
      setUrl(`https://${r.data.public_domain}`);
    });
  }, []);
  useEffect(() => {
    if (url) QRCode.toDataURL(url, { width: 640, margin: 1, color: { dark: "#1E3A8A", light: "#ffffff" } }).then(setQr).catch((e) => console.warn("QR generation failed", e));
  }, [url]);
  const logoUrl = logo.startsWith("http") ? logo : window.location.origin + logo;
  const esc = (s) => String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  const hexToRgb = (h) => {
    const m = h.replace("#", "");
    return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
  };
  const loadImg = (src) => new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });
  const downloadPosterPdf = async () => {
    const pdf = new jsPDF("p", "mm", "a4");
    const W = 210, H = 297;
    pdf.setFillColor(...hexToRgb(T.bg));
    pdf.rect(0, 0, W, H, "F");
    try {
      const li = await loadImg(logoUrl);
      const lh = 16, lw = lh * (li.width / li.height || 4);
      if (theme === "scuro") {
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect((W - lw - 10) / 2, 24, lw + 10, lh + 8, 3, 3, "F");
      }
      pdf.addImage(li, "PNG", (W - lw) / 2, 28, lw, lh);
    } catch (e) { console.warn("Poster logo could not be embedded", e); }
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(...hexToRgb(T.eyebrow));
    pdf.text("SKIN ANALYZER", W / 2, 58, { align: "center", charSpace: 1.4 });
    pdf.setFontSize(24);
    pdf.setTextColor(...hexToRgb(T.ink));
    const hl = pdf.splitTextToSize("Scopri cosa possiamo osservare della tua pelle.", 150);
    pdf.text(hl, W / 2, 74, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.setTextColor(...hexToRgb(T.ink2));
    const sub = pdf.splitTextToSize("Fotografie guidate e alcune domande per creare il tuo profilo cosmetico visivo.", 140);
    pdf.text(sub, W / 2, 74 + hl.length * 9 + 6, { align: "center" });
    const qrSize = 72, qrY = 150;
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect((W - qrSize - 14) / 2, qrY - 7, qrSize + 14, qrSize + 14, 4, 4, "F");
    if (qr) pdf.addImage(qr, "PNG", (W - qrSize) / 2, qrY, qrSize, qrSize);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.setTextColor(...hexToRgb(T.cta));
    pdf.text("Inquadra il codice con la fotocamera del telefono", W / 2, qrY + qrSize + 18, { align: "center", maxWidth: 150 });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(...hexToRgb(T.meta));
    pdf.text("Circa 3 minuti · Non serve registrarsi", W / 2, qrY + qrSize + 26, { align: "center" });
    pdf.setFontSize(10);
    pdf.text(`${settings.store_name} · ${settings.store_address}`, W / 2, 276, { align: "center" });
    pdf.text(url, W / 2, 282, { align: "center", maxWidth: 170 });
    pdf.save("poster-skin-analyzer.pdf");
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = qr;
    a.download = "qr-skin-analyzer.png";
    a.click();
  };
  const printPoster = () => {
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>Skin Analyzer</title>
      <style>@page{size:A4;margin:0}*{box-sizing:border-box;margin:0}
      body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;background:${T.bg};color:${T.ink};text-align:center;padding:64px 56px;display:flex;flex-direction:column;align-items:center;min-height:100vh;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .eyebrow{letter-spacing:.22em;font-size:15px;color:${T.eyebrow};font-weight:800;margin-top:22px}
      h1{font-size:40px;line-height:1.15;font-weight:800;margin:26px 0 10px;max-width:620px;color:${T.ink}}
      p.sub{font-size:18px;color:${T.ink2};max-width:560px;margin:0 auto}
      .qr{margin:44px auto 20px;padding:22px;border:2px solid ${T.qrBorder};border-radius:24px;display:inline-block;background:#fff}
      .qr img{width:320px;height:320px;display:block}
      .cta{font-size:20px;font-weight:700;color:${T.cta}}
      .meta{font-size:15px;color:${T.meta};margin-top:10px}
      .foot{margin-top:auto;padding-top:40px;font-size:14px;color:${T.meta};border-top:1px solid ${T.border};width:100%}
      </style></head><body>
      <img src="${esc(logoUrl)}"${theme === "scuro" ? ' style="height:64px;object-fit:contain;background:#fff;padding:10px 16px;border-radius:14px"' : ' style="height:64px;object-fit:contain"'} crossorigin="anonymous"/>
      <div class="eyebrow">SKIN ANALYZER</div>
      <h1>Scopri cosa racconta la tua pelle.</h1>
      <p class="sub">Fotografie guidate e alcune domande per creare il tuo profilo cosmetico visivo.</p>
      <div class="qr"><img src="${esc(qr)}"/></div>
      <div class="cta">Inquadra il codice con la fotocamera del telefono</div>
      <div class="meta">Circa 3 minuti · Non serve registrarsi</div>
      <div class="foot">${esc(settings?.store_name || "Farma Store")} · ${esc(settings?.store_address || "")}<br/>${esc(url)}</div>
      <script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
      </body></html>`;
    // Use a Blob URL instead of document.write (avoids the document.write API).
    const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const w = window.open(blobUrl, "_blank", "width=820,height=1160");
    if (!w) {
      URL.revokeObjectURL(blobUrl);
      return;
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  };
  if (!settings) return null;
  return (
    <div className="max-w-2xl" data-testid="qr-poster">
      <p className="text-sm text-[var(--fs-ink-2)] mb-4">
        Genera un poster con codice QR da esporre al banco: il cliente lo inquadra e apre lo Skin Check direttamente sul telefono.
      </p>
      <div className="fs-card p-5">
        <label className="text-xs font-medium text-[var(--fs-muted)] uppercase tracking-wide">Indirizzo di destinazione</label>
        <input value={url} onChange={(e) => setUrl(e.target.value)} className="w-full mt-1 p-3 rounded-xl border border-[var(--fs-border)] text-sm bg-white" data-testid="qr-url-input" />
        <div className="flex flex-wrap gap-2 mt-2">
          <button onClick={() => setUrl(`https://${settings.public_domain}`)} className="px-3 py-1.5 rounded-full text-xs font-medium border border-[var(--fs-border)] bg-white" data-testid="qr-use-domain">Usa dominio pubblico</button>
          <button onClick={() => setUrl(window.location.origin)} className="px-3 py-1.5 rounded-full text-xs font-medium border border-[var(--fs-border)] bg-white" data-testid="qr-use-current">Usa indirizzo attuale</button>
        </div>

        <div className="mt-4">
          <label className="text-xs font-medium text-[var(--fs-muted)] uppercase tracking-wide">Tema poster</label>
          <div className="flex gap-2 mt-1.5">
            {[["chiaro", "Chiaro"], ["scuro", "Scuro"]].map(([v, l]) => (
              <button key={v} onClick={() => setTheme(v)}
                className={`px-4 py-2 rounded-full text-sm font-medium border ${theme === v ? "bg-[var(--fs-blue)] text-white border-[var(--fs-blue)]" : "bg-white border-[var(--fs-border)] text-[var(--fs-ink-2)]"}`}
                data-testid={`poster-theme-${v}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-col items-center">
          {qr && <img src={qr} alt="QR" className="w-56 h-56 rounded-2xl border border-[var(--fs-border)] p-2 bg-white" data-testid="qr-preview" />}
          <p className="text-xs text-[var(--fs-muted)] mt-3 break-all text-center">{url}</p>
        </div>

        <div className="mt-6">
          <label className="text-xs font-medium text-[var(--fs-muted)] uppercase tracking-wide">Anteprima poster</label>
          <div className="mt-2 mx-auto w-full max-w-[300px] rounded-2xl px-6 py-7 text-center shadow-sm" style={{ background: T.bg, border: `1px solid ${T.border}` }} data-testid="poster-preview">
            <img src={logoUrl} alt="" className="h-7 mx-auto object-contain" style={theme === "scuro" ? { background: "#fff", padding: "5px 9px", borderRadius: 8 } : {}} />
            <div className="text-[10px] tracking-[0.22em] font-extrabold mt-3" style={{ color: T.eyebrow }}>SKIN ANALYZER</div>
            <div className="text-[15px] font-extrabold leading-tight mt-2" style={{ color: T.ink }}>Scopri cosa possiamo osservare della tua pelle.</div>
            <div className="text-[10px] mt-1.5 leading-snug" style={{ color: T.ink2 }}>Fotografie guidate e alcune domande per creare il tuo profilo cosmetico visivo.</div>
            {qr && <div className="mt-3 inline-block p-2 rounded-xl bg-white" style={{ border: `1px solid ${T.qrBorder}` }}><img src={qr} alt="QR" className="w-28 h-28" /></div>}
            <div className="text-[11px] font-bold mt-2" style={{ color: T.cta }}>Inquadra il codice con la fotocamera</div>
            <div className="text-[9px] mt-1" style={{ color: T.meta }}>Circa 3 minuti · Non serve registrarsi</div>
            <div className="text-[9px] mt-3 pt-2" style={{ color: T.meta, borderTop: `1px solid ${T.border}` }}>{settings.store_name} · {settings.store_address}</div>
          </div>
          <p className="text-xs text-[var(--fs-muted)] mt-2 text-center">Il poster stampato avrà lo stesso layout in formato A4.</p>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={download} className="fs-btn-ghost flex-1 py-3 flex items-center justify-center gap-2 fs-focus" data-testid="qr-download-button">
            <DownloadIcon size={16} /> Scarica QR
          </button>
          <button onClick={printPoster} className="fs-btn-primary flex-1 py-3 flex items-center justify-center gap-2 fs-focus" data-testid="qr-print-button">
            <Printer size={16} /> Stampa poster
          </button>
        </div>
        <button onClick={downloadPosterPdf} className="fs-btn-ghost w-full py-3 mt-3 flex items-center justify-center gap-2 fs-focus" data-testid="poster-pdf-button">
          <DownloadIcon size={16} /> Scarica poster PDF
        </button>
      </div>
    </div>
  );
}

function PrivacyTech() {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/admin/tech-docs").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const rows = [
    ["Architettura", d.architecture],
    ["Pipeline vision (doppio modello)", d.vision_pipeline],
    ["Regole di consenso", d.consensus_rules],
    ["Quality gate", d.quality_gate],
    ["Flusso immagini e retention", d.image_flow_retention],
    ["Validazione output", d.output_validation],
    ["Modificare WhatsApp", d.how_to_edit_whatsapp],
    ["Cambiare modalità", d.how_to_change_mode],
    ["Disattivare un parametro", d.how_to_disable_parameter],
    ["Limiti noti", d.limitations],
  ];
  return (
    <div className="space-y-3 max-w-3xl" data-testid="tech-docs">
      <div className="fs-card p-4 text-sm">Modalità attuale: <b>{d.app_mode}</b> · Confidenza minima: <b>{d.min_confidence}</b></div>
      {rows.map(([t, c]) => (
        <div key={t} className="fs-card p-4">
          <h4 className="font-semibold text-sm mb-1">{t}</h4>
          <p className="text-sm text-[var(--fs-ink-2)] leading-relaxed">{c}</p>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("fs_admin_token"));
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (authed) adminApi.get("/admin/me").catch(() => { localStorage.removeItem("fs_admin_token"); setAuthed(false); });
  }, [authed]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const logout = () => { localStorage.removeItem("fs_admin_token"); setAuthed(false); };

  return (
    <div className="min-h-[100dvh] bg-[var(--fs-page)]">
      <header className="bg-white border-b border-[var(--fs-border)] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo className="h-7" />
            <span className="fs-eyebrow text-[var(--fs-muted)]">Admin</span>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-[var(--fs-ink-2)] fs-focus rounded" data-testid="admin-logout-button">
            <LogOut size={16} /> Esci
          </button>
        </div>
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-[var(--fs-blue)] text-[var(--fs-blue)]" : "border-transparent text-[var(--fs-ink-2)]"}`}
              data-testid={`tab-${t.id}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6">
        {tab === "overview" && <Overview />}
        {tab === "validation" && <Validation />}
        {tab === "parameters" && <Parameters />}
        {tab === "stats" && <Stats />}
        {tab === "settings" && <SettingsTab />}
        {tab === "poster" && <QRPoster />}
        {tab === "preview" && <AnteprimaReport />}
        {tab === "privacy" && <PrivacyTech />}
      </main>
    </div>
  );
}
